import * as React from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { AeroexpressTrip, AeroexpressSelectedTrip, useSaveOrderServicesMutation, MoneyFragment } from '@websky/graphql';
import { CdnIconNames } from '@shared/ui';
import CheckoutAdditionalService from '../../CheckoutAdditionalService/CheckoutAdditionalService';
import { getAeroexpress, getOrder } from '../../../Checkout/store/order/selectors';
import Warning from '../../../Checkout/components/Checkout/Upsale/Warning/Warning';
import { ThemeProvider, useTheme } from '../../../theme';
import { currentCurrency } from '../../../utils';
import icons from '../../icons';
import { fillOrder } from '../../../Checkout/store/order/actions';
import { useAeroexpress } from '../../../Aeroexpress/components/hooks';
import { getAeroexpressPrice } from '../../../Checkout/store/orderPrice/selectors';
import AeroexpressPopup from '../AeroexpressPopup/AeroexpressPopup';
import { MoneyType } from 'PriceMatrix/types';
import { AeroexpressFragment } from '@websky/graphql';
import { useRenderers } from '../../../renderProps';
import { CartServiceAeroexpress, OnDeleteItemType } from '../../../Cart/types';
import { ServiceSummary } from '../../../features/service-summary';
import { ServiceButton } from '../../types';
import { ModalX } from '../../../BaseComponents';
import { useQueryEventToggleable } from '../../../Checkout/components/Checkout/hooks';
import { SpecialServiceType } from '../../../AIPurchasePredictionService/types';

interface Props {
	aggregationMode?: boolean;
	aeroexpress?: AeroexpressFragment['aeroexpress'];
	isSelected?: boolean;
	icons?: React.ReactNode[] | CdnIconNames[];
	segmentId?: string;
}

const AeroexpressAdditionalService: React.FC<Props> = ({
	aggregationMode,
	aeroexpress: aeroexpressProps,
	isSelected: isSelectedProps,
	icons: iconsProps,
	segmentId
}) => {
	const { t } = useTranslation('Checkout');
	const { Aeroexpress: cardStyles } = useTheme('CheckoutAdditionalService');
	const { Aeroexpress: css } = useTheme('Aeroexpress');

	const dispatch = useDispatch();

	const order = useSelector(getOrder);
	const aeroexpress = aggregationMode
		? (aeroexpressProps as AeroexpressFragment['aeroexpress'])
		: (useSelector(getAeroexpress) as AeroexpressFragment['aeroexpress']);

	const isSelected = aggregationMode ? isSelectedProps : useSelector(getAeroexpressPrice)?.amount > 0;
	const selectedTrips = aeroexpress?.selectedTrips;

	const minPrice = useMemo<MoneyType>(() => {
		let minAmount = aeroexpress?.trips[0]?.price?.amount;

		for (const trip of aeroexpress?.trips) {
			if (trip?.price?.amount < minAmount) {
				minAmount = trip.price.amount;
			}
		}

		return {
			amount: minAmount,
			currency: aeroexpress?.trips?.[0]?.price.currency || currentCurrency
		};
	}, [aeroexpress]);

	const selectedTripsPrice = selectedTrips.reduce<MoneyFragment>(
		(price, selectedTrip) => ({
			amount: Number(selectedTrip.price.amount) + Number(price.amount),
			currency: currentCurrency
		}),
		{
			amount: 0,
			currency: currentCurrency
		}
	);

	const [saveOrderServicesMutation, { loading }] = useSaveOrderServicesMutation();

	const selectAeroexpress = async (ids: string[]) => {
		try {
			const { data } = await saveOrderServicesMutation({
				variables: {
					params: {
						id: order.id,
						aeroexpress: { selectedTrips: [...ids] }
					}
				}
			});

			dispatch(fillOrder(data?.SaveOrderServices));
			close();
		} catch (e) {
			console.log(e);
		}
	};

	const {
		showWarning,
		servicesRedirectURL,
		closeWarning,
		buttonText,
		isOpen,
		close,
		handleOpenModalClick
	} = useAeroexpress(selectAeroexpress, order.id);

	useQueryEventToggleable({
		serviceTypes: [SpecialServiceType.Aeroexpress],
		onOpen: handleOpenModalClick
	});

	const onAeroexpressItemRemove: OnDeleteItemType = (_, payload) => {
		if (aeroexpress.selectedTrips.length) {
			const filterTripIds = aeroexpress.selectedTrips
				.filter(trip => trip.id !== (payload as CartServiceAeroexpress).tripId)
				.map(trip => trip.id);

			selectAeroexpress(filterTripIds);
		}
	};

	const buttons = useMemo(() => {
		const btns: ServiceButton[] = [
			{
				action: handleOpenModalClick,
				isLoading: loading,
				text: buttonText,
				buttonType: 'change'
			}
		];

		if (!loading) {
			btns.push({
				action: () => window.open('https://aeroexpress.ru', '_blank'),
				text: t('Aeroexpress:View schedule')
			});
		}

		return btns;
	}, [buttonText, loading, handleOpenModalClick]);

	const AdditionalServiceRender = useRenderers()?.CheckoutAdditionalService?.CheckoutAdditionalService;
	const ServiceWrapper = AdditionalServiceRender ?? CheckoutAdditionalService;

	return (
		<>
			<ServiceSummary serviceType="Aeroexpress" onDelete={onAeroexpressItemRemove} segmentId={segmentId}>
				{content => (
					<ServiceWrapper
						isLoading={loading}
						className={cardStyles.aeroexpress}
						header={t('Aeroexpress')}
						isSelected={isSelected}
						priceFrom={minPrice as MoneyType}
						priceTotal={selectedTripsPrice}
						mode={aggregationMode ? 'aggregator' : 'default'}
						description={t(
							'Aeroexpress:No worries about traffic jams, arrival at the airport strictly on schedule'
						)}
						addClick={handleOpenModalClick}
						coverClassName={cardStyles.aeroexpress_aggregator}
						icon={icons.transferIcon}
						aggregationIcons={iconsProps}
						buttons={buttons}
					>
						<ThemeProvider
							value={{
								ServiceSummary: {
									ServiceSummaryOption: {
										option: css.option
									}
								}
							}}
						>
							{content}
						</ThemeProvider>
					</ServiceWrapper>
				)}
			</ServiceSummary>

			<ModalX
				size="large"
				isOpen={isOpen}
				classes={{
					scrollBody: css.aeroexpress__scrollBody,
					paper: cn(css.aeroexpress_paper, cardStyles.aeroexpress_paper__large)
				}}
				onClose={close}
			>
				<AeroexpressPopup
					trips={aeroexpress.trips as AeroexpressTrip[]}
					selectedTrips={aeroexpress.selectedTrips as AeroexpressSelectedTrip[]}
					isLoading={loading}
					onSubmit={selectAeroexpress}
					onClose={close}
				/>
			</ModalX>

			<Warning open={showWarning} redirectURL={servicesRedirectURL} onClose={closeWarning} />
		</>
	);
};

export default AeroexpressAdditionalService;
